import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { relatedTitles } from "@/mocks/relatedTitles";

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "只做一部剧",
    text: "全站内容严格围绕「朋友的妈妈4」展开，不做综合影视库，所有页面、推荐位与搜索范围都限定在该剧之内。",
  },
  {
    icon: "ri-layout-masonry-line",
    title: "清晰的信息结构",
    text: "剧集目录、分集详情、剧情介绍三层结构，方便观众按集数快速定位，或先读简介再决定从哪一集开始。",
  },
  {
    icon: "ri-eye-line",
    title: "观看体验优先",
    text: "深色界面降低长时间浏览的视觉疲劳，播放区、章节导航与收藏按钮都放在拇指易触达的位置。",
  },
];

export default function AboutPage() {
  usePageMeta(
    "关于本站 - 朋友的妈妈4影视站",
    "朋友的妈妈4影视站专注该剧内容的专题站点介绍，包含站点定位、内容说明、版权声明与免责声明。",
    "朋友的妈妈4影视站,朋友的妈妈4介绍,朋友的妈妈4关于,朋友的妈妈4免责声明",
  );

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pb-10">
        <section className="pt-24 md:pt-28">
          <div className="max-w-[1280px] mx-auto px-4 md:px-8">
            <nav
              aria-label="面包屑"
              className="flex items-center gap-2 text-xs text-foreground-600"
            >
              <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-900">关于</span>
            </nav>

            <div className="mt-6 rounded-lg bg-background-100 border border-background-200/70 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-6 md:p-10">
                  <p className="text-[11px] tracking-[0.25em] uppercase text-primary-400">
                    About this site
                  </p>
                  <h1 className="mt-3 font-heading text-3xl md:text-4xl leading-tight text-foreground-950">
                    朋友的妈妈4影视站
                    <span className="block mt-1 text-lg md:text-xl text-foreground-500 font-normal">
                      专注该剧内容的专题站点
                    </span>
                  </h1>
                  <p className="mt-5 text-sm leading-8 text-foreground-500">
                    这是一个以单部剧集为主题的影视内容站。我们把「朋友的妈妈4」全 16 集的目录、分集简介、
                    人物关系与剧情文本整理在同一套页面结构里，让观众不用在杂乱的信息流里翻找。
                  </p>
                  <p className="mt-3 text-sm leading-8 text-foreground-500">
                    站点不提供真实视频资源，播放区域为前端演示界面；收藏功能仅保存在你自己的浏览器中，
                    不需要注册，也不会上传任何个人信息。
                  </p>

                  <div className="mt-7 flex flex-wrap gap-3">
                    <Link
                      to="/episodes"
                      className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-list-check-2" />
                      浏览全部剧集
                    </Link>
                    <Link
                      to="/detail/ep-01"
                      className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300/60 text-foreground-800 text-sm hover:border-accent-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      从第 1 集开始
                    </Link>
                  </div>
                </div>

                <div className="relative min-h-[240px] lg:min-h-full bg-background-950">
                  <img
                    src="https://readdy.ai/api/search-image?query=cinematic%20moody%20film%20still%20of%20old%20apartment%20windows%20at%20dusk%20with%20warm%20amber%20light%2C%20silhouettes%20of%20curtains%20moving%2C%20deep%20charcoal%20shadows%2C%20quiet%20nostalgic%20atmosphere%2C%20cinematic%20grain%2C%20no%20text&width=1000&height=800&seq=fm4-about-01&orientation=landscape"
                    alt="朋友的妈妈4 站点视觉图"
                    title="朋友的妈妈4影视站 关于页面"
                    className="absolute inset-0 w-full h-full object-cover object-top opacity-80"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/30 to-transparent lg:bg-gradient-to-r lg:from-background-100 lg:via-background-950/20 lg:to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-8 mt-10 md:mt-14">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {positioning.map((item) => (
              <div
                key={item.title}
                className="rounded-lg bg-background-100 border border-background-200/70 p-5 hover:border-accent-500/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-md bg-accent-100 text-accent-500 flex items-center justify-center">
                  <i className={`${item.icon} text-lg`} />
                </div>
                <h2 className="mt-4 font-heading text-base text-foreground-950">{item.title}</h2>
                <p className="mt-2 text-xs leading-6 text-foreground-500">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-8 mt-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-4">
              <article className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
                <h2 className="font-heading text-lg text-foreground-950 flex items-center gap-2">
                  <i className="ri-mail-send-line text-accent-500" />
                  联系方式
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-500">
                  本站为内容演示站点，如对页面内容、排版或有合作意向，可通过以下方式留言。以下信息仅作展示，
                  页面不提供在线提交表单。
                </p>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <li className="rounded-md bg-background-200/50 p-3">
                    <p className="text-[11px] text-foreground-600">联系邮箱</p>
                    <p className="mt-1 text-sm text-foreground-900 break-all">
                      contact@example-drama.site
                    </p>
                  </li>
                  <li className="rounded-md bg-background-200/50 p-3">
                    <p className="text-[11px] text-foreground-600">内容纠错</p>
                    <p className="mt-1 text-sm text-foreground-900 break-all">
                      fix@example-drama.site
                    </p>
                  </li>
                </ul>
              </article>

              <article className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
                <h2 className="font-heading text-lg text-foreground-950 flex items-center gap-2">
                  <i className="ri-copyright-line text-accent-500" />
                  版权声明
                </h2>
                <div className="mt-3 space-y-3 text-sm leading-7 text-foreground-500">
                  <p>
                    本站页面中的剧集名称、剧情文本、图片素材均为演示内容，用于展示页面结构与排版效果，
                    不代表真实剧集信息，也不构成任何形式的资源分发。
                  </p>
                  <p>
                    「朋友的妈妈4」相关名称与内容的版权归原制作方及权利人所有。本站不存储、不制作、
                    不传播任何视频文件，如权利人认为页面内容不妥，可通过上方邮箱联系，我们将在核实后及时处理。
                  </p>
                </div>
              </article>

              <article className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
                <h2 className="font-heading text-lg text-foreground-950 flex items-center gap-2">
                  <i className="ri-shield-check-line text-accent-500" />
                  免责声明
                </h2>
                <div className="mt-3 space-y-3 text-sm leading-7 text-foreground-500">
                  <p>
                    详情页的播放区域为前端静态演示界面，未接入任何真实视频播放接口，点击播放只会显示提示说明，
                    不会产生任何播放行为。
                  </p>
                  <p>
                    收藏功能基于浏览器本地存储实现，清除浏览器数据后收藏记录将一并消失，站点不承担数据丢失的责任。
                    站内推荐位为排版演示，不指向其他影视作品页面。
                  </p>
                </div>
              </article>
            </div>

            <aside className="lg:col-span-4 space-y-4">
              <div className="rounded-lg bg-background-100 border border-background-200/70 p-4">
                <h2 className="font-heading text-sm tracking-wide text-foreground-950 mb-3">
                  站点信息
                </h2>
                <ul className="space-y-2.5 text-xs">
                  {[
                    { icon: "ri-film-line", label: "收录内容", value: "朋友的妈妈4 · 16 集" },
                    { icon: "ri-calendar-line", label: "建站时间", value: "2026年6月" },
                    { icon: "ri-play-circle-line", label: "播放方式", value: "演示播放器" },
                    { icon: "ri-heart-line", label: "收藏方式", value: "浏览器本地存储" },
                    { icon: "ri-smartphone-line", label: "适配范围", value: "桌面端 / 手机端" },
                  ].map((item) => (
                    <li key={item.label} className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-foreground-600">
                        <i className={`${item.icon} text-accent-500`} />
                        {item.label}
                      </span>
                      <span className="text-foreground-900 text-right">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg bg-background-100 border border-background-200/70 p-4">
                <h2 className="font-heading text-sm tracking-wide text-foreground-950 mb-3">
                  相关推荐（演示）
                </h2>
                <ul className="space-y-2">
                  {relatedTitles.map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center gap-2.5 rounded-md bg-background-200/40 p-2"
                    >
                      <div className="w-8 h-11 rounded overflow-hidden bg-background-900 shrink-0">
                        <img
                          src={item.cover}
                          alt={item.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-foreground-900 truncate">{item.title}</p>
                        <p className="text-[10px] text-foreground-600 truncate">{item.genre}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-[11px] leading-5 text-foreground-600">
                  推荐位仅用于展示排版效果，不提供跳转。
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}